import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, X, Users, Car, Flame, Sun, Waves, DollarSign } from "lucide-react";
import cabanaImg from "@/assets/cabanas/whats-included.jpg";
import picnicImg from "@/assets/gallery/clear-river.jpg";

const CANONICAL = "/cabana-vs-picnic-table";
const TITLE = "Cabana vs Picnic Table on the Guadalupe River";
const DESCRIPTION =
  "Cabana or in-water picnic table at Son's Guadalupe? Side-by-side: capacity, shade, grill, parking, and price for each riverside rental.";

const cabanaProduct = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Private Riverside Cabana on the Guadalupe River",
  description:
    "Shaded private cabana on the Guadalupe River with charcoal BBQ grill, picnic table, dedicated parking, ceiling fans, and direct river access. Up to 12 guests.",
  category: "Lodging > Cabana Rental",
  brand: { "@type": "Brand", name: "Son's Guadalupe" },
  image: "https://sonsguadalupe.com/og-image.png",
  offers: {
    "@type": "AggregateOffer",
    lowPrice: 69,
    highPrice: 899,
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    url: "https://sonsguadalupe.com/cabanas",
  },
};

const picnicProduct = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "In-Water Covered Picnic Table on the Guadalupe River",
  description:
    "Covered in-water picnic table rental on the Guadalupe River at Son's Guadalupe. Includes 6 guests and 1 car parking. Perfect for smaller groups who want their feet in the river while they eat.",
  category: "Recreation > Riverside Picnic Rental",
  brand: { "@type": "Brand", name: "Son's Guadalupe" },
  image: "https://sonsguadalupe.com/og-image.png",
  offers: {
    "@type": "Offer",
    price: 69,
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    url: "https://sonsguadalupe.com/cabanas#picnic-tables",
  },
};

const breadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
    { "@type": "ListItem", position: 2, name: "Cabanas", item: "https://sonsguadalupe.com/cabanas" },
    { "@type": "ListItem", position: 3, name: "Cabana vs Picnic Table", item: `https://sonsguadalupe.com${CANONICAL}` },
  ],
};

const rows: { label: string; icon: typeof Users; cabana: string | true; picnic: string | true }[] = [
  { label: "Guests included", icon: Users, cabana: "Up to 12 (8 included)", picnic: "6 included" },
  { label: "Parking included", icon: Car, cabana: "1 spot (extra avail.)", picnic: "1 spot" },
  { label: "Shade", icon: Sun, cabana: "Covered cabana + ceiling fans", picnic: "Covered roof over water" },
  { label: "BBQ grill", icon: Flame, cabana: true, picnic: "Shared grills nearby" },
  { label: "Feet in the river", icon: Waves, cabana: "Steps away", picnic: "Yes — table sits in the water" },
  { label: "Starting price", icon: DollarSign, cabana: "$69+", picnic: "$69" },
];

const CabanaVsPicnicTable = () => (
  <div className="min-h-screen">
    <SEOHead
      title={TITLE}
      description={DESCRIPTION}
      canonical={CANONICAL}
      schemaJson={[cabanaProduct, picnicProduct, breadcrumb]}
    />
    <Header />

    <main className="container-narrow mx-auto px-4 md:px-8 pt-28 pb-16">
      <header className="mb-10 max-w-3xl">
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-charcoal mb-4">
          Cabana vs Picnic Table on the Guadalupe — Which Should You Book?
        </h1>
        <p className="text-charcoal/70 text-lg">
          Both are riverside, both are private to your group, both start at $69. Here's the
          honest difference so you book the right one.
        </p>
      </header>

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        <div className="rounded-2xl overflow-hidden shadow-soft bg-white">
          <img src={cabanaImg} alt="Private riverside cabana at Son's Guadalupe" className="w-full h-64 object-cover" />
          <div className="p-6">
            <h2 className="font-heading text-2xl font-bold text-charcoal mb-2">Private Cabana</h2>
            <p className="text-charcoal/70 mb-4">
              Your own shaded basecamp on the bank. Picnic table, charcoal grill, ceiling fans,
              ample room for a bigger group, and the float ends right here.
            </p>
            <Link to="/cabanas">
              <Button variant="hero" size="lg" className="w-full">See cabanas</Button>
            </Link>
          </div>
        </div>

        <div className="rounded-2xl overflow-hidden shadow-soft bg-white">
          <img src={picnicImg} alt="In-water covered picnic table on the Guadalupe River" className="w-full h-64 object-cover" />
          <div className="p-6">
            <h2 className="font-heading text-2xl font-bold text-charcoal mb-2">In-Water Picnic Table</h2>
            <p className="text-charcoal/70 mb-4">
              A covered picnic table that literally sits <em>in</em> the river. Eat with your feet
              in the water. Best for groups of up to 6 who want river contact while they eat.
            </p>
            <Link to="/cabanas#picnic-tables">
              <Button variant="hero-outline" size="lg" className="w-full">See picnic tables</Button>
            </Link>
          </div>
        </div>
      </div>

      <section className="mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-charcoal mb-6">
          Side-by-side comparison
        </h2>
        <div className="overflow-x-auto rounded-xl border border-charcoal/10">
          <table className="w-full text-left">
            <thead className="bg-cream">
              <tr>
                <th className="p-4 font-heading">Feature</th>
                <th className="p-4 font-heading">Cabana</th>
                <th className="p-4 font-heading">Picnic Table</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className="border-t border-charcoal/10">
                  <td className="p-4 flex items-center gap-2 text-charcoal/80">
                    <r.icon className="w-4 h-4 text-teal" /> {r.label}
                  </td>
                  <td className="p-4 text-charcoal/80">
                    {r.cabana === true ? <Check className="w-5 h-5 text-teal" /> : r.cabana}
                  </td>
                  <td className="p-4 text-charcoal/80">
                    {r.picnic === true ? <Check className="w-5 h-5 text-teal" /> : r.picnic}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12 grid md:grid-cols-2 gap-6">
        <div className="rounded-xl bg-teal/5 p-6">
          <h3 className="font-heading text-xl font-bold text-charcoal mb-2">Book the cabana if…</h3>
          <ul className="space-y-2 text-charcoal/80">
            <li className="flex gap-2"><Check className="w-5 h-5 text-teal flex-shrink-0" /> You have 7+ guests</li>
            <li className="flex gap-2"><Check className="w-5 h-5 text-teal flex-shrink-0" /> You want a full BBQ + day basecamp</li>
            <li className="flex gap-2"><Check className="w-5 h-5 text-teal flex-shrink-0" /> You're tubing and want the float to end at your spot</li>
            <li className="flex gap-2"><Check className="w-5 h-5 text-teal flex-shrink-0" /> You need shaded seating for naps and snacks</li>
          </ul>
        </div>
        <div className="rounded-xl bg-coral-orange/5 p-6">
          <h3 className="font-heading text-xl font-bold text-charcoal mb-2">Book the picnic table if…</h3>
          <ul className="space-y-2 text-charcoal/80">
            <li className="flex gap-2"><Check className="w-5 h-5 text-coral-orange flex-shrink-0" /> Your group is 6 or fewer</li>
            <li className="flex gap-2"><Check className="w-5 h-5 text-coral-orange flex-shrink-0" /> You want to literally eat in the river</li>
            <li className="flex gap-2"><Check className="w-5 h-5 text-coral-orange flex-shrink-0" /> You're here for a few hours, not a full day</li>
            <li className="flex gap-2"><X className="w-5 h-5 text-charcoal/40 flex-shrink-0" /> You don't need your own grill</li>
          </ul>
        </div>
      </section>

      <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
        <Link to="/book-now/sons-guadalupe">
          <Button variant="hero" size="xl" className="w-full">Book a cabana</Button>
        </Link>
        <Link to="/cabanas#picnic-tables">
          <Button variant="hero-outline" size="xl" className="w-full">Book a picnic table</Button>
        </Link>
      </div>
    </main>

    <Footer />
  </div>
);

export default CabanaVsPicnicTable;
